/**
 * GridMind — Portal do Colaborador (Riscos Psicossociais, NR-1).
 * Acessível a qualquer usuário autenticado (NÃO exige o módulo GridMind).
 * Lista as campanhas publicadas e permite responder pelo portal interno.
 * Em campanha anônima NUNCA solicita identidade.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Brain, CheckCircle2, AlertCircle, Loader2, ShieldCheck, ArrowLeft, ClipboardList } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PortalGridMind = () => {
  const [loading, setLoading] = useState(true);
  const [campanhas, setCampanhas] = useState([]);
  const [erroLista, setErroLista] = useState(null);
  const [selecionada, setSelecionada] = useState(null); // id da campanha em resposta

  const carregarLista = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/gridmind/portal/campanhas`);
      setCampanhas(r.data || []);
    } catch (e) {
      setErroLista(e.response?.data?.detail || 'Não foi possível carregar as pesquisas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { carregarLista(); }, []);

  if (selecionada) {
    return (
      <Responder
        cid={selecionada}
        onVoltar={() => { setSelecionada(null); carregarLista(); }}
      />
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-600 rounded-lg"><Brain className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Pesquisas de Riscos Psicossociais</h1>
          <p className="text-sm text-slate-500">Participe das avaliações em aberto da sua organização (NR-1).</p>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      )}

      {!loading && erroLista && (
        <div className="bg-red-50 text-red-700 rounded-md p-3 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {erroLista}
        </div>
      )}

      {!loading && !erroLista && campanhas.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            Nenhuma pesquisa em aberto no momento.
          </CardContent>
        </Card>
      )}

      {!loading && campanhas.map(c => (
        <Card key={c.id} className="border-indigo-100">
          <CardContent className="py-4 flex items-center justify-between gap-4">
            <div>
              <p className="font-semibold text-slate-800">{c.codigo}</p>
              <p className="text-sm text-slate-500">{c.descricao || 'Pesquisa de Riscos Psicossociais (NR-1)'}</p>
              <span className={`inline-flex items-center gap-1 mt-1 text-xs ${c.anonima ? 'text-emerald-700' : 'text-amber-700'}`}>
                <ShieldCheck className="w-3 h-3" />
                {c.anonima ? 'Anônima' : 'Identificada'}
              </span>
            </div>
            {c.ja_respondida ? (
              <span className="text-sm text-emerald-600 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" /> Respondida
              </span>
            ) : (
              <Button onClick={() => setSelecionada(c.id)} className="bg-indigo-600 hover:bg-indigo-700">Responder</Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const Responder = ({ cid, onVoltar }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [campanha, setCampanha] = useState(null);
  const [completed, setCompleted] = useState(false);

  const [respostas, setRespostas] = useState({});
  const [abertas, setAbertas] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/gridmind/portal/campanhas/${cid}`);
        setCampanha(r.data);
        if (r.data?.ja_respondida) setCompleted(true);
      } catch (e) {
        setError(e.response?.data?.detail || 'Pesquisa indisponível.');
      } finally {
        setLoading(false);
      }
    })();
  }, [cid]);

  const likertLabels = campanha?.metodologia?.likert_labels || ['Nunca', 'Raramente', 'Às vezes', 'Frequentemente', 'Sempre'];

  const todasPerguntas = () => {
    const ids = [];
    (campanha?.metodologia?.escalas || []).forEach(e =>
      (e.fatores || []).forEach(f => (f.perguntas || []).forEach(p => ids.push(p))));
    (campanha?.perguntas_customizadas || []).filter(p => p.tipo !== 'aberta').forEach(p => ids.push(p));
    return ids;
  };
  const totalObrig = () => todasPerguntas().filter(p => p.obrigatoria !== false).length;
  const respondidasObrig = () => todasPerguntas().filter(p => p.obrigatoria !== false && respostas[p.id]).length;

  const enviar = async () => {
    const faltando = todasPerguntas().filter(p => p.obrigatoria !== false && !respostas[p.id]);
    if (faltando.length > 0) {
      setError(`Responda todas as ${totalObrig()} perguntas obrigatórias (faltam ${faltando.length}).`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        respostas: Object.entries(respostas).map(([pergunta_id, valor]) => ({ pergunta_id, valor: Number(valor) })),
        respostas_abertas: Object.entries(abertas)
          .filter(([, t]) => (t || '').trim())
          .map(([pergunta_id, texto]) => ({ pergunta_id, texto })),
        origem: 'portal',
      };
      // Em campanha identificada o servidor associa a resposta ao usuário autenticado.
      await axios.post(`${API}/api/gridmind/campanhas/${cid}/responder`, payload);
      setCompleted(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erro ao enviar respostas.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (completed) {
    return (
      <div className="max-w-md mx-auto py-12 text-center">
        <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Resposta registrada!</h2>
        <p className="text-slate-600 mb-6">Obrigado por contribuir com a avaliação de riscos psicossociais da sua organização.</p>
        <Button variant="outline" onClick={onVoltar}><ArrowLeft className="w-4 h-4 mr-2" /> Voltar às pesquisas</Button>
      </div>
    );
  }

  if (error && !campanha) {
    return (
      <div className="max-w-md mx-auto py-12 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-slate-700 mb-6">{error}</p>
        <Button variant="outline" onClick={onVoltar}><ArrowLeft className="w-4 h-4 mr-2" /> Voltar</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Button variant="ghost" onClick={onVoltar} className="text-slate-600"><ArrowLeft className="w-4 h-4 mr-2" /> Voltar às pesquisas</Button>

      <Card className="border-indigo-100">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-600 rounded-lg"><Brain className="w-6 h-6 text-white" /></div>
            <div>
              <CardTitle className="text-xl">{campanha.codigo}</CardTitle>
              <CardDescription>{campanha.descricao || 'Pesquisa de Riscos Psicossociais (NR-1)'}</CardDescription>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-md p-2">
            <ShieldCheck className="w-4 h-4" />
            {campanha.anonima
              ? 'Pesquisa anônima — sua identidade não é registrada.'
              : 'Pesquisa identificada — suas respostas são vinculadas ao seu cadastro.'}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Responda com sinceridade. Escala: <b>1 = {likertLabels[0]}</b> até <b>5 = {likertLabels[4]}</b>.
          </p>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 text-red-700 rounded-md p-3 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {(campanha.metodologia?.escalas || []).map(escala => (
        <Card key={escala.id}>
          <CardHeader>
            <CardTitle className="text-base">{escala.nome}</CardTitle>
            {escala.descricao && <CardDescription>{escala.descricao}</CardDescription>}
          </CardHeader>
          <CardContent className="space-y-5">
            {(escala.fatores || []).map(fator => (
              <div key={fator.id} className="space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">{fator.nome}</p>
                {(fator.perguntas || []).map(p => (
                  <Likert key={p.id} pergunta={p} labels={likertLabels}
                          valor={respostas[p.id]} onChange={v => setRespostas({ ...respostas, [p.id]: v })} />
                ))}
              </div>
            ))}
          </CardContent>
        </Card>
      ))}

      {(campanha.perguntas_customizadas || []).filter(p => p.tipo !== 'aberta').length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Perguntas da Empresa</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {(campanha.perguntas_customizadas || []).filter(p => p.tipo !== 'aberta').map(p => (
              <Likert key={p.id} pergunta={p} labels={likertLabels}
                      valor={respostas[p.id]} onChange={v => setRespostas({ ...respostas, [p.id]: v })} />
            ))}
          </CardContent>
        </Card>
      )}

      {(campanha.perguntas_abertas || []).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Comentários</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {(campanha.perguntas_abertas || []).map(p => (
              <div key={p.id}>
                <Label className="text-sm">{p.texto}</Label>
                <Textarea value={abertas[p.id] || ''} onChange={e => setAbertas({ ...abertas, [p.id]: e.target.value })}
                          rows={3} placeholder="Sua resposta (opcional)" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="sticky bottom-4">
        <Card className="shadow-lg">
          <CardContent className="py-4 flex items-center justify-between gap-4">
            <span className="text-sm text-slate-600">{respondidasObrig()}/{totalObrig()} obrigatórias respondidas</span>
            <Button onClick={enviar} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Enviar respostas
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const Likert = ({ pergunta, labels, valor, onChange }) => (
  <div className="border-b border-slate-100 pb-3">
    <p className="text-sm text-slate-800 mb-2">
      {pergunta.texto}
      {pergunta.obrigatoria !== false && <span className="text-red-500 ml-1">*</span>}
    </p>
    <div className="flex flex-wrap gap-2">
      {[1, 2, 3, 4, 5].map(v => (
        <button key={v} type="button" onClick={() => onChange(v)}
          className={`flex-1 min-w-[90px] text-xs rounded-md border px-2 py-2 transition-colors ${
            Number(valor) === v
              ? 'bg-indigo-600 text-white border-indigo-600'
              : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
          }`}>
          <span className="font-semibold block">{v}</span>
          <span className="block">{labels[v - 1]}</span>
        </button>
      ))}
    </div>
  </div>
);

export default PortalGridMind;
